// Streaming client for /api/outlook/draft-reply.
// Uses fetch+ReadableStream because EventSource only supports GET.
// Each SSE `data:` line is a JSON-encoded DraftStreamChunk.

import { getStoredSession } from "./api";

export type DraftTone = "formal" | "neutral" | "informal";
export type DraftLength = "short" | "medium" | "long";

export interface DraftEmailContext {
  fromEmail: string;
  fromDisplayName?: string;
  subject: string;
  bodyText: string;
  receivedAt?: string;
}

export interface DraftMessage {
  role: "user" | "assistant";
  content: string;
}

export interface DraftRequest {
  caseId: string;
  email: DraftEmailContext;
  history?: DraftMessage[];
  instruction?: string;
  tone?: DraftTone;
  length?: DraftLength;
}

export type DraftStreamChunk =
  | { type: "text"; delta: string }
  | { type: "tool_call"; name: string }
  | { type: "tool_result"; name: string; ok: boolean }
  | { type: "done"; finalText: string }
  | { type: "error"; message: string };

const API_BASE = "https://app.casemeister.com";

export async function* streamDraftReply(
  request: DraftRequest,
  signal?: AbortSignal,
): AsyncGenerator<DraftStreamChunk, void, void> {
  const session = getStoredSession();
  if (!session) throw new Error("Niet ingelogd");

  const res = await fetch(`${API_BASE}/api/outlook/draft-reply`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: `Bearer ${session}`,
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) msg = body.error;
    } catch { /* non-JSON body */ }
    throw new Error(msg);
  }
  if (!res.body) throw new Error("Geen response-stream");

  // Standard SSE parser: messages are separated by blank lines, each
  // line is "field: value". We only emit `data:` payloads (single channel).
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Parse complete events
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const rawEvent = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);

        for (const line of rawEvent.split("\n")) {
          if (!line.startsWith("data:")) continue; // ignore comments / heartbeats
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            yield JSON.parse(payload) as DraftStreamChunk;
          } catch {
            // Bad payload — skip but don't kill the stream
          }
        }
      }
    }
  } finally {
    try { reader.releaseLock(); } catch { /* already released */ }
  }
}
